import { useMemo, useSyncExternalStore } from 'react';

/**
 * AB#2193 (DS-0 W6b) — design tokens for code that cannot use `var(--…)`:
 * canvas charts (Chart.js) and SVG chart props (Recharts), whose colours must be
 * concrete values. Reads the semantic tokens from src/styles/tokens.css off
 * <body> and re-reads them when <body>'s class changes (theme toggle), so charts
 * follow light/dark like the rest of the UI. HTML/SVG elements should keep using
 * `var(--token)` in styles instead.
 */

/** Current value of a token (name without the leading `--`), or `fallback` when unresolved (e.g. jsdom). */
export function readToken(name: string, fallback = ''): string {
    if (typeof document === 'undefined' || !document.body) return fallback;
    const value = getComputedStyle(document.body).getPropertyValue(`--${name}`).trim();
    return value || fallback;
}

function subscribe(onChange: () => void): () => void {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => undefined;
    const observer = new MutationObserver(onChange);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
}

const bodyClass = (): string => (typeof document === 'undefined' ? '' : document.body.className);

/**
 * Resolves token names to concrete colours and re-renders on theme change.
 * `useTokenColors(['primary', 'text-secondary'])` → `{ primary: '#6138e0', 'text-secondary': '#525c6b' }`.
 */
export function useTokenColors<K extends string>(names: readonly K[]): Record<K, string> {
    const cls = useSyncExternalStore(subscribe, bodyClass, () => '');
    const key = names.join('|');
    return useMemo(
        () => Object.fromEntries(names.map((n) => [n, readToken(n)])) as Record<K, string>,
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the stable identity of `names`; `cls` re-reads on theme change
        [cls, key],
    );
}

/**
 * `#rgb` / `#rrggbb` → `rgba(r, g, b, alpha)`; any other colour string is returned unchanged.
 * An unresolved (empty) token gives `transparent`, which canvas APIs such as addColorStop accept.
 */
export function withAlpha(color: string, alpha: number): string {
    if (!color.trim()) return 'transparent';
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
    if (!m) return color;
    const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
